import '../../Admin.scss';
import '../../../../styles/_table.scss';

import { Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ReactComponent as ArrowDownIcon } from 'images/icons/ArrowDown.svg';
import { ReactComponent as ArrowUpIcon } from 'images/icons/ArrowUp.svg';
import { DefaultRoutes } from 'Routes';
import DeleteListItemModal from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import { ReactComponent as InfoIcon } from 'images/icons/Info.svg';
import { LanguageType } from 'types/languages';
import { Link } from 'react-router-dom';
import Paragraph from 'components/TextComps/Paragraph';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import Tooltip from '@trackman/web-shared-components/components/Tooltip/Tooltip';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import dayjs from 'dayjs';
import { getLanguageShortText } from 'utilities/languages';
import { mapBooleanToString } from 'utilities/booleanMapper';
import { observer } from 'mobx-react-lite';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { useStores } from 'index';

type RowsDisplay = {
  id: string;
  title: string;
  description: string;
  languages: string;
  visible: string;
  comingSoon: string;
  createdAt: string;
  updatedAt: string;
};

export const StudyCoursesList = observer(() => {
  useSetPageTitle({ title: 'Study Courses', subtitle: '' });
  const studyCoursesStore = useStores().studyroomCourseEditorStore;
  const [isModalOn, setIsModalOn] = useState(false);

  const deleteCourse = useCallback(() => {
    studyCoursesStore.deleteStudyCourseAndUpdate();
    setIsModalOn(false);
  }, [studyCoursesStore, setIsModalOn]);

  useEffect(() => {
    studyCoursesStore.studyCoursesList.length === 0 && studyCoursesStore.getStudyCourses();
  }, [studyCoursesStore]);

  const rowsDisplay: RowsDisplay[] = useMemo(
    () =>
      studyCoursesStore.studyCoursesList.map((course) => {
        return {
          id: course.id,
          title: course.title.toLowerCase(),
          description: course.descriptions.find((desc) => desc.countryCode === 'en-US')?.text,
          languages: course.languages ? course.languages.map((lng: LanguageType) => getLanguageShortText(lng)).join(', ') : '',
          visible: mapBooleanToString(course.visible),
          comingSoon: mapBooleanToString(course.comingSoon),
          createdAt: dayjs(course.createdAt).format('MMM DD, YYYY'),
          updatedAt: dayjs(course.updatedAt).format('MMM DD, YYYY'),
        };
      }) as RowsDisplay[],
    [studyCoursesStore.studyCoursesList]
  );

  return (
    <div className='admin-content-container'>
      <div className='admin-list-header'>
        <HeaderCreateButton buttonTitle='Create new' route={DefaultRoutes.StudyCoursesEditor} />
      </div>

      <div className='tmu-table-wrapper'>
        <Table className='tmu-table admin-table'>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Languages</TableCell>
              <TableCell>Visible</TableCell>
              <TableCell>Coming soon</TableCell>
              <TableCell>Date created</TableCell>
              <TableCell>Last edited</TableCell>
              <TableCell colSpan={1}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsDisplay.length ? (
              rowsDisplay.map((row, index) => {
                return (
                  <TableRow hover tabIndex={-1} key={row.id}>
                    <TableCell>
                      <div className='description-tooltip'>
                        {row.title}
                        <Tooltip
                          title={row.description ? row.description : 'No description'}
                          placement='bottom'
                          className='table-cell-tooltip'
                        >
                          <InfoIcon />
                        </Tooltip>
                      </div>
                    </TableCell>
                    <TableCell className='languages'>{row.languages}</TableCell>
                    <TableCell>{row.visible}</TableCell>
                    <TableCell>{row.comingSoon}</TableCell>
                    <TableCell>{row.createdAt}</TableCell>
                    <TableCell>{row.updatedAt}</TableCell>
                    <TableCell>
                      <div className='action-buttons'>
                        <Link to={`/Admin/${DefaultRoutes.StudyCoursesEditor}/${row.id}`} className='row-button'>
                          <PencilIcon />
                        </Link>
                        <button
                          className='row-button'
                          style={index === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          onClick={() => (index !== 0 ? studyCoursesStore.updateSortOrder(row.id, true) : {})}
                        >
                          <ArrowUpIcon />
                        </button>
                        <button
                          className='row-button'
                          style={index === studyCoursesStore.studyCoursesList.length - 1 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          onClick={() =>
                            index !== studyCoursesStore.studyCoursesList.length - 1 ? studyCoursesStore.updateSortOrder(row.id, false) : {}
                          }
                        >
                          <ArrowDownIcon />
                        </button>
                        <button
                          className='row-button'
                          onClick={() => {
                            studyCoursesStore.selectedCourseId = row.id;
                            setIsModalOn(true);
                          }}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <Paragraph>
                    <>No courses found</>
                  </Paragraph>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DeleteListItemModal isModalOn={isModalOn} setIsModalOn={setIsModalOn} deleteAction={deleteCourse} itemName='course' />
    </div>
  );
});

export default StudyCoursesList;
